import { createRouter, createMemoryHistory, createWebHistory, createWebHashHistory } from 'vue-router'
import { Platform } from 'quasar'

import connectRoutes from './portals/connect'
import businessRoutes from './portals/business'

export const SANDBOX_BUSINESS_PATH = 'business'
export const SANDBOX_CONNECT_PATH = 'connect'

export const SUBDOMAIN_BUSINESS_URL = process.env.SUBDOMAIN_BUSINESS_URL
export const SUBDOMAIN_CONNECT_URL = process.env.SUBDOMAIN_CONNECT_URL

const siteUrl = process.env.SITE_URL
// To force a subdomain, use one of the above SUBDOMAIN constants
const forceSubdomainMode = null
const useSubdomains = process.env.USE_SUBDOMAINS || false

/* Shared Routes */
const View404 = () => import('components/statuspages/404.vue')
const SandboxIndex = () => import('components/SandboxIndex.vue')

export let Router

/**
 * Correctly generate a route list based on the current portal the user is in
 * @returns array
 */
const routeList = () => {
  const hostParts = window.location.host.split('.')
  const domainPrefix = forceSubdomainMode || (useSubdomains && hostParts.length > 0 && hostParts[0] !== 'www' ? hostParts[0] : null)

  let list = []

  if (Platform.is.nativeMobile || domainPrefix) {
    if (!Platform.is.nativeMobile && ((domainPrefix && domainPrefix.includes(SUBDOMAIN_CONNECT_URL)))) {
      list = connectRoutes()
    }
    else if (Platform.is.nativeMobile || (domainPrefix && domainPrefix.includes(SUBDOMAIN_BUSINESS_URL))) {
      list = businessRoutes()
    }
  }

  if (list.length < 1 && !useSubdomains) {
    list = [
      {
        path: '/',
        name: 'SandboxIndex',
        meta: {
          title: 'Sandbox',
          loggedIn: 0
        },
        component: SandboxIndex
      },
      ...businessRoutes(SANDBOX_BUSINESS_PATH),
      ...connectRoutes(SANDBOX_CONNECT_PATH)
    ]
  }

  return [
    ...list,
    {
      path: '/:catchAll(.*)*',
      name: 'Error404',
      meta: {
        title: 'Error 404',
        loggedIn: 0
      },
      component: View404
    }
  ]
}

/**
 * Grab the current baseURL for the portal the user is within
 * @returns {string}
 */
export const baseUrl = () => {
  const hostParts = window.location.host.split('.')
  const domainPrefix = forceSubdomainMode || (useSubdomains && hostParts.length > 0 && hostParts[0] !== 'www' ? hostParts[0] : null)

  if (Platform.is.nativeMobile || domainPrefix) {
    return '/'
  }

  if (!useSubdomains) {
    // get path after hostname
    const pathname = window.location.pathname

    if (pathname.includes(`/${SANDBOX_CONNECT_PATH}/`)) {
      return `/${SANDBOX_CONNECT_PATH}/`
    }
    else if (pathname.includes(`/${SANDBOX_BUSINESS_PATH}/`)) {
      return `/${SANDBOX_BUSINESS_PATH}/`
    }
  }

  return '/'
}

/**
 * Generate a business URL for specifically redirecting a user to it
 * @returns {string}
 */
export const generateBusinessUrl = () => {
  if (forceSubdomainMode) {
    return siteUrl
  }

  if (useSubdomains) {
    const URL_PARTS = siteUrl.split('://')
    const hostParts = URL_PARTS[1].split('.')

    hostParts.unshift(SUBDOMAIN_BUSINESS_URL)
    return URL_PARTS[0] + '://' + hostParts.join('.')
  }

  return siteUrl + '/' + SANDBOX_BUSINESS_PATH
}

/**
 * Generate a connect URL for specifically redirecting a user to it
 * @returns {string}
 */
export const generateConnectUrl = () => {
  if (forceSubdomainMode) {
    return siteUrl
  }

  if (useSubdomains) {
    const URL_PARTS = siteUrl.split('://')
    const hostParts = URL_PARTS[1].split('.')

    hostParts.unshift(SUBDOMAIN_CONNECT_URL)
    return URL_PARTS[0] + '://' + hostParts.join('.')
  }

  return siteUrl + '/' + SANDBOX_CONNECT_PATH
}

export default function ({ store }) {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === 'history' ? createWebHistory : createWebHashHistory

  Router = createRouter({
    scrollBehavior: (to, from, savedPosition) => {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (savedPosition) {
            resolve(savedPosition)
          }
          else if (to.hash) {
            resolve({
              selector: to.hash
            })
          }
          else {
            resolve({ left: 0, top: 0 })
          }
        }, 50)
      })
    },
    routes: routeList(),

    // Leave this as is and make changes in quasar.config.js instead!
    // quasar.config.js -> build -> vueRouterMode
    // quasar.config.js -> build -> publicPath
    history: createHistory(process.env.VUE_ROUTER_BASE)
  })

  Router.beforeEach(async (to, from, next) => {
    const checkPermission = function () {
      if ('permission' in to.meta) {
        let permissionList = to.meta.permission
        if (!(permissionList instanceof Array)) {
          permissionList = [permissionList]
        }

        for (const perm of permissionList) {
          if (!store.getters.hasPermission(perm)) {
            return false
          }
        }
      }
      return true
    }

    if (to.meta.loggedIn === -1) {
      if (store.getters.isLoggedIn === true) {
        next(baseUrl() + 'home')
        return
      }
    }
    else if (to.meta.loggedIn === 1) {
      if (store.getters.isLoggedIn === false) {
        next(baseUrl())
        return
      }

      const permission = checkPermission()
      if (!permission) {
        next(baseUrl() + '404')
        return
      }
    }

    next()
  })

  return Router
}
